import React, { useRef } from "react";
import { ChevronDown } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { applyDataTableRowCheckboxSelection } from "@/components/ui/data-table-row-selection";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface NotificationEventAccountPickerProps {
  accountCodeOptions: string[];
  selectedCodes: string[];
  onChange: (codes: string[]) => void;
}

const NotificationEventAccountPicker: React.FC<NotificationEventAccountPickerProps> = ({
  accountCodeOptions,
  selectedCodes,
  onChange,
}) => {
  const lastSelectedCodeRef = useRef<string | null>(null);
  const shiftHandledRef = useRef(false);

  const triggerLabel =
    selectedCodes.length === 0
      ? "Choose accounts…"
      : selectedCodes.length === 1
        ? selectedCodes[0]
        : `${selectedCodes.length} accounts selected`;

  const applyToggle = (
    accountCode: string,
    selected: boolean,
    shiftKey: boolean,
  ) => {
    const previous: Record<string, boolean> = {};
    selectedCodes.forEach((code) => {
      previous[code] = true;
    });
    const { next, nextAnchorRowId } = applyDataTableRowCheckboxSelection({
      previous,
      visibleRowIds: accountCodeOptions,
      anchorRowId: lastSelectedCodeRef.current,
      targetRowId: accountCode,
      selected: shiftKey ? true : selected,
      shiftKey,
    });
    lastSelectedCodeRef.current = nextAnchorRowId;
    if (shiftKey) shiftHandledRef.current = true;
    onChange(Object.keys(next).filter((code) => next[code]));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          className="h-8 w-fit max-w-full min-w-[10rem] justify-between gap-2 font-normal"
          title={selectedCodes.length > 0 ? selectedCodes.join(", ") : undefined}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className={cn(
          "w-auto min-w-[var(--radix-popover-trigger-width)] max-w-sm gap-0 p-2"
        )}
        data-notification-event-account-picker=""
      >
        <div className="flex max-h-56 flex-col gap-1 overflow-y-auto select-none">
          {accountCodeOptions.map((accountCode) => {
            const checkboxId = `notification-account-${accountCode}`;
            const isChecked = selectedCodes.includes(accountCode);

            return (
              <label
                key={accountCode}
                htmlFor={checkboxId}
                className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50"
                onPointerDownCapture={(e) => {
                  if (e.button !== 0) return;
                  if (!e.shiftKey) {
                    shiftHandledRef.current = false;
                    return;
                  }
                  // Own Shift+range so Radix focus-only behavior does not win.
                  e.preventDefault();
                  e.stopPropagation();
                  applyToggle(accountCode, true, true);
                }}
              >
                <Checkbox
                  id={checkboxId}
                  checked={isChecked}
                  onCheckedChange={(checked) => {
                    if (shiftHandledRef.current) {
                      shiftHandledRef.current = false;
                      return;
                    }
                    applyToggle(accountCode, checked === true, false);
                  }}
                  data-broker-asset-class-checkbox=""
                />
                <span className={cn(BROKER_FIELD_DESC_CLASS, "cursor-pointer")}>{accountCode}</span>
              </label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationEventAccountPicker;
